<template>
  <li class="tree-node" :style="{ paddingLeft: depth * 16 + 'px' }">
    <div
      class="node-header"
      :class="{
        'folder-open': isFolder && node.open,
        'file-item': !isFolder,
        selected: isSelected,
      }"
      @click="handleClick"
      @dblclick="handleDoubleClick"
      @contextmenu.prevent="handleContextMenu"
    >
      <span
        v-if="isFolder && hasChildren"
        class="toggle-icon"
        :class="{ open: node.open }"
        @click.stop="toggleFolder"
      >
        <i class="fa-solid fa-angle-right"></i>
      </span>
      <span v-else class="toggle-icon"></span>

      <i :class="iconClass" class="node-icon"></i>

      <span class="node-name">{{ displayName }}</span>

      <small v-if="!isFolder && node.size" class="file-size">
        ({{ formatSize(node.size) }})
      </small>

      <small v-if="!isFolder && node.last_modified" class="file-modified">
        Ultima modifica: {{ node.last_modified }}
      </small>
    </div>

    <ul v-if="isFolder && node.open" class="tree-children">
      <li v-if="showEmptyMessage && !hasChildren" class="empty-folder-message">
        <i class="fa-solid fa-circle-info"></i>
        Nessun file presente
      </li>
      <TreeNode
        v-for="child in node.children"
        :key="child.rel_path"
        :node="child"
        :depth="depth + 1"
        :selected-path="selectedPath"
        :label-resolver="labelResolver"
        :show-empty-message="showEmptyMessage"
        :toggle-on-folder-click="toggleOnFolderClick"
        @open-file="$emit('open-file', $event)"
        @select-folder="$emit('select-folder', $event)"
        @toggle-folder="$emit('toggle-folder', $event)"
        @folder-context-menu="$emit('folder-context-menu', $event)"
      />
    </ul>
  </li>
</template>

<script>
export default {
  name: "TreeNode",
  props: {
    node: {
      type: Object,
      required: true,
    },
    depth: {
      type: Number,
      required: true,
    },
    selectedPath: {
      type: String,
      default: "",
    },
    labelResolver: {
      type: Function,
      default: null,
    },
    showEmptyMessage: {
      type: Boolean,
      default: true,
    },
    toggleOnFolderClick: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["open-file", "select-folder", "toggle-folder", "folder-context-menu"],

  computed: {
    displayName() {
      return this.labelResolver
        ? this.labelResolver(this.node)
        : this.node.name;
    },
    isFolder() {
      return this.node.type === "folder";
    },
    isSelected() {
      return this.isFolder && this.node.rel_path === this.selectedPath;
    },
    hasChildren() {
      return Array.isArray(this.node.children) && this.node.children.length > 0;
    },
    iconClass() {
      if (this.isFolder) {
        return this.node.open ? "fas fa-folder-open" : "fas fa-folder";
      }
      const ext = (this.node.name.split(".").pop() || "").toLowerCase();
      const map = {
        csv: "fas fa-file-csv",
        xlsx: "fas fa-file-excel",
        xls: "fas fa-file-excel",
        pdf: "fas fa-file-pdf",
        txt: "fas fa-file-lines",
        doc: "fas fa-file-word",
        docx: "fas fa-file-word",
        jpg: "fas fa-file-image",
        jpeg: "fas fa-file-image",
        png: "fas fa-file-image",
        gif: "fas fa-file-image",
        zip: "fas fa-file-zipper",
      };
      return map[ext] || "fas fa-file";
    },
  },

  methods: {
    handleClick() {
      if (this.isFolder) {
        if (this.toggleOnFolderClick) {
          this.toggleFolder();
        } else {
          this.$emit("select-folder", this.node);
        }
      } else {
        this.$emit("open-file", this.node.rel_path);
      }
    },

    handleDoubleClick() {
      if (this.isFolder && !this.toggleOnFolderClick) {
        this.toggleFolder();
      }
    },

    toggleFolder() {
      if (this.isFolder) {
        this.$emit("toggle-folder", this.node);
      }
    },

    handleContextMenu(event) {
      if (!this.isFolder) return;
      this.$emit("folder-context-menu", {
        node: this.node,
        x: event.clientX,
        y: event.clientY,
      });
    },

    formatSize(bytes) {
      if (!bytes) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
    },
  },
};
</script>

<style scoped>
.tree-node {
  margin: 1px 0;
}

.node-header {
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 3px 8px;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.15s;
  user-select: none;
}

.node-header:hover {
  background: rgba(0, 120, 215, 0.1);
}

.node-header.file-item:hover {
  background: rgba(0, 120, 215, 0.15);
}

.node-header.selected {
  background: rgba(0, 120, 215, 0.2);
  outline: 1px solid rgba(0, 120, 215, 0.45);
}

.toggle-icon {
  width: 16px;
  height: 16px;
  margin-right: 4px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  color: #555;
}

.toggle-icon.open {
  transform: rotate(90deg);
}

.node-icon {
  width: 16px;
  height: 16px;
  margin-right: 6px;
  font-size: 14px;
  color: #0078d4;
}

.node-name {
  flex: 1;
  font-size: 13.5px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.file-size {
  color: #777;
  font-size: 11.5px;
  margin-left: 8px;
}

.file-modified {
  flex: 0 0 auto;
  margin-left: auto;
  padding-left: 16px;
  color: #7b8794;
  font-size: 11.5px;
  font-style: italic;
  white-space: nowrap;
}

.tree-children {
  list-style: none;
  padding: 0;
  margin: 0 0 0 12px;
  border-left: 1px dashed #ddd;
}

.empty-folder-message {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  color: #7b8794;
  font-size: 13px;
  font-style: italic;
  text-align: left;
}

.empty-folder-message i {
  color: #9aa5b1;
  font-size: 12px;
}
</style>
